import {chunk} from 'lodash';
import {useCallback, useEffect, useReducer, useRef} from 'react';

/**
 * If the input arg is true, a user will be confronted with the "are you sure
 * you want to navigate away from this page" system dialog box if they attempt
 * to navigate.
 *
 * @param warn
 */
function useNavigateAwayWarning(warn: boolean) {
  useEffect(() => {
    if (warn) {
      window.onbeforeunload = () => {
        return true;
      };

      return () => {
        window.onbeforeunload = null;
      };
    }
    return () => {};
  }, [warn]);
}

const PARALLELISM = 16;

class Cancellation extends Error {}

type RunAsync<In, Out> = (
  input: In,
  index: number,
  items: In[]
) => Promise<Out>;

/**
 * Given a list of items and a function that processes them async, process those
 * items using a parallelism algorithm. This is intended to be used with some
 * sort of server request, where we should make multiple calls to the server at
 * the same time, but that parallelism should be bounded by some reasonable
 * upper limit.
 *
 * Returns an AsyncGenerator of the resulting promises, in the order that the
 * items appear passed in.
 *
 * @deprecated use in unit tests only please
 * @argument items
 * @argument runAsync
 */
export async function* doRunAsync<In, Out>(
  items: In[],
  runAsync: RunAsync<In, Out>
): AsyncGenerator<Awaited<Result<In, Out>>> {
  const chunks = chunk(items, PARALLELISM);
  let index = 0;

  const success = (item: In) => (result: Out) => ({
    status: 'success' as const,
    item,
    result,
  });

  const failure = (item: In) => (error: Error) => ({
    status: 'failure' as const,
    item,
    error,
  });

  // TODO: This uses a simple but naive algorithm to salvage some parallelism
  //       out of the promise-based API here, but it doesn't have the highest
  //       throughput possible. To improve throughput, there should be a
  //       continuously-filling/draining bucket of in-flight requests, up to the
  //       maximum parallelism. In practice the chunking approach works fine for
  //       now, though.
  //       The other deficit in this approach is that cancelling will cancel
  //       future chunks from being moved, but will not stop the current chunk
  //       from completing.
  for (const ch of chunks) {
    const promises = [];
    for (const c of ch) {
      const request = runAsync(c, index, items).then(success(c), failure(c));
      yield request;
      index += 1;
      promises.push(request);
    }

    await Promise.allSettled(promises);
  }
}

type Result<In, Out> =
  | {
      status: 'success';
      item: In;
      result: Out;
    }
  | {
      status: 'failure';
      item: In;
      error: Error;
    };

type ReducerState<In, Out> =
  | {
      status: 'idle';
    }
  | {
      status: 'running' | 'cancelled' | 'complete';
      startedAt: number;
      total: number;
      progress: number;
      results: Result<In, Out>[];
    };

type ActionType<In, Out> =
  | {type: 'start'; payload: {total: number}}
  | {type: 'progress'; payload: {result: Result<In, Out>}}
  | {type: 'cancel'}
  | {type: 'complete'};

const INITIAL_STATE: ReducerState<unknown, unknown> = {
  status: 'idle',
};

function asyncBatcherReducer<In, Out>(
  state: ReducerState<In, Out>,
  action: ActionType<In, Out>
): ReducerState<In, Out> {
  switch (action.type) {
    case 'start': {
      if (state.status === 'running') {
        throw new TypeError(`Start cannot be issued if already running`);
      }
      return {
        status: 'running',
        total: action.payload.total,
        progress: 0,
        startedAt: Date.now(),
        results: [],
      };
    }
    case 'progress': {
      if (state.status !== 'running') {
        throw new TypeError(`Progress cannot be issued if not running`);
      }
      return {
        ...state,
        progress: state.progress + 1,
        results: state.results.concat(action.payload.result),
      };
    }
    case 'cancel': {
      if (state.status !== 'running') {
        throw new TypeError(`Cancel cannot be issued if not running`);
      }
      return {
        ...state,
        status: 'cancelled',
      };
    }
    case 'complete': {
      if (state.status === 'cancelled') {
        // Cannot mark a completed batch run as cancelled. If something tries,
        // ignore it.
        return state;
      }
      if (state.status !== 'running') {
        throw new TypeError(`Complete cannot be issued if not running`);
      }
      return {
        ...state,
        status: 'complete',
      };
    }
  }
}

type AsyncBatcherOpts = {
  showNavigationAwayWarning?: boolean;
};
export type UseAsyncBatcherHookState<In, Out> = ReducerState<In, Out> & {
  cancel: () => Promise<void>;
};
type UseAsyncBatcherHook<In, Out> = [
  (items: In[]) => Promise<void>,
  UseAsyncBatcherHookState<In, Out>
];

/**
 * Provide a function that performs some async operation, this will return an
 * executor that will run that async operation in batches over the list of
 * input items. In-progress work can be cancelled with the provided cancellation
 * function.
 *
 * Unmounting the component that contains this hook will also cancel an
 * in-progress operation.
 *
 * @example
 * ```ts
 * const [doMath, outcome] = useAsyncBatcher(
 *   (n: number) => doExpensiveMathWith(n)
 * )
 *
 * await doMath(manyManyNumbers)
 * // outcome.results has the result of the processing
 * ```
 *
 * @param runAsync
 * @param options
 */
export function useAsyncBatcher<In, Out>(
  runAsync: RunAsync<In, Out>,
  options: AsyncBatcherOpts = {}
): UseAsyncBatcherHook<In, Out> {
  const [state, dispatch] = useReducer(
    asyncBatcherReducer<In, Out>,
    INITIAL_STATE as ReducerState<In, Out>
  );
  const runAsyncFnRef = useRef(runAsync);
  const runGeneratorRef = useRef<null | AsyncGenerator>(null);

  useEffect(() => {
    runAsyncFnRef.current = runAsync;
  }, [runAsync]);

  useEffect(
    () => () => {
      if (runGeneratorRef.current) {
        // stop generator on component unmount:
        runGeneratorRef.current.return(undefined);
        runGeneratorRef.current = null;
      }
    },
    []
  );

  // If the batcher is running, and nav-away-warning is true, show a dialog
  // warning the user about navigating away, if they try to do so.
  useNavigateAwayWarning(
    Boolean(options.showNavigationAwayWarning) && state.status === 'running'
  );

  const execute = useCallback(async (items: In[]) => {
    if (runGeneratorRef.current) {
      // If you've hit this error, consider cancelling first.
      throw new Error(
        `Cannot start the async batcher if it's already running!`
      );
    }
    const generator = doRunAsync(items, runAsyncFnRef.current);
    runGeneratorRef.current = generator;

    dispatch({type: 'start', payload: {total: items.length}});
    try {
      for await (const result of generator) {
        dispatch({type: 'progress', payload: {result}});
      }
      dispatch({type: 'complete'});
    } catch (e) {
      if (e instanceof Cancellation) {
        dispatch({type: 'cancel'});
      } else {
        throw e;
      }
    } finally {
      runGeneratorRef.current = null;
    }
  }, []);
  const cancel = useCallback(async () => {
    if (!runGeneratorRef.current) {
      return;
    }
    await runGeneratorRef.current.throw(new Cancellation());
  }, []);

  return [execute, {...state, cancel}];
}
